/**
 * Status Color Mappings
 * Centralized status-to-MUI-chip-color maps for consistent UI across all pages.
 */

/** General status colors (contracts, approvals, etc.) */
const STATUS_COLORS = {
  // Contract statuses
  draft: 'default',
  'pending approval': 'warning',
  approved: 'info',
  active: 'success',
  matured: 'secondary',
  'matured - rolled': 'default',
  'matured - terminated': 'error',
  'early terminated': 'error',
  terminated: 'error',
  cancelled: 'error',
  historical: 'default',
  extended: 'info',
  rolled: 'info',
  finalised: 'info',

  // Approval statuses
  pending: 'warning',
  pending_review: 'info',
  verified: 'success',
  rejected: 'error',
  queried: 'secondary',
  reversed: 'default',
  executed: 'success',

  // Payment statuses
  matched: 'success',
  unmatched: 'warning',
  partial: 'info',
  overdue: 'error',

  // Generic
  inactive: 'default',
  expired: 'error',
  suspended: 'warning',
  completed: 'success',
};

/**
 * Get MUI Chip color for a status string.
 * Case-insensitive lookup.
 *
 * @param {string} status - Status string
 * @returns {string} MUI color name ('default', 'primary', 'secondary', 'error', 'info', 'success', 'warning')
 */
export const getStatusColor = (status) =>
  STATUS_COLORS[status?.toLowerCase()] || 'default';

/**
 * Priority color mapping (for approval priorities).
 *
 * @param {string} priority - 'critical', 'high', 'normal', 'low'
 * @returns {string} MUI color name
 */
export const getPriorityColor = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'critical': return 'error';
    case 'high': return 'warning';
    case 'normal': return 'default';
    case 'low': return 'info';
    default: return 'default';
  }
};

/**
 * FICA compliance status color.
 *
 * @param {string} status - 'compliant', 'pending', 'non_compliant', 'expired'
 * @returns {string} MUI color name
 */
export const getFicaColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'compliant': return 'success';
    case 'pending': return 'warning';
    case 'non_compliant': return 'error';
    case 'expired': return 'error';
    default: return 'default';
  }
};

/**
 * License status color.
 *
 * @param {string} status - 'active', 'suspended', 'expired', 'revoked'
 * @returns {string} MUI color name
 */
export const getLicenseColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'active': return 'success';
    case 'suspended': return 'warning';
    case 'expired': return 'error';
    case 'revoked': return 'error';
    default: return 'default';
  }
};

const statusColorUtils = {
  getStatusColor,
  getPriorityColor,
  getFicaColor,
  getLicenseColor,
};

export default statusColorUtils;
